import { useReducer, useEffect, useState, useCallback, useRef } from 'react'
import { httpClient } from '@/lib/api/http-client'
import { NotificationsListResponse, NotificationUnreadCountResponse } from '@digital-twins/shared-types'
import {
  notificationsReducer,
  initialState,
} from '@/lib/reducers/notifications.reducer'
import {
  shouldUseDevMockNotifications,
  getDevMockLastReadAt,
  getDevMockNotifications,
} from '@/lib/dev/mock-notifications'

export function useNotificationsData(
  isPlatformAdmin?: boolean,
  email?: string | null,
) {
  const [state, dispatch] = useReducer(notificationsReducer, initialState)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const loadingMore = useRef(false)

  useEffect(() => {
    let cancelled = false
    const useDevMocks = shouldUseDevMockNotifications(isPlatformAdmin, email)

    async function fetchInitial() {
      try {
        setIsLoading(true)
        setError(null)

        const [listRes, countRes] = await Promise.all([
          httpClient.get<NotificationsListResponse>('/notifications', { params: { limit: 20 } }),
          httpClient.get<NotificationUnreadCountResponse>('/notifications/unread-count'),
        ])

        if (cancelled) return

        const list = listRes.data
        const mockItems = useDevMocks ? getDevMockNotifications() : []
        const lastReadAt = useDevMocks
          ? getDevMockLastReadAt(mockItems)
          : Math.max(list.lastReadAt, countRes.data.lastReadAt)

        dispatch({
          type: 'LOADED_PAGE',
          items: [...mockItems, ...list.items],
          cursor: list.cursor,
          hasMore: list.hasMore,
          lastReadAt,
        })
      } catch (err) {
        if (cancelled) return

        if (useDevMocks) {
          const mockItems = getDevMockNotifications()
          dispatch({
            type: 'LOADED_PAGE',
            items: mockItems,
            cursor: undefined,
            hasMore: false,
            lastReadAt: getDevMockLastReadAt(mockItems),
          })
          return
        }

        setError(err instanceof Error ? err.message : 'Failed to load notifications')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchInitial()
    return () => { cancelled = true }
  }, [isPlatformAdmin, email])

  const loadMore = useCallback(async () => {
    if (!state.hasMore || !state.cursor || loadingMore.current) return
    loadingMore.current = true

    try {
      const res = await httpClient.get<NotificationsListResponse>('/notifications', {
        params: { cursor: state.cursor, limit: 20 },
      })

      dispatch({
        type: 'LOAD_MORE',
        items: res.data.items,
        cursor: res.data.cursor,
        hasMore: res.data.hasMore,
      })
    } catch {
      // Silently ignore load-more failures
    } finally {
      loadingMore.current = false
    }
  }, [state.hasMore, state.cursor])

  const markAllRead = useCallback(async () => {
    const latestCreatedAt = state.items.reduce(
      (max, item) => Math.max(max, item.createdAt),
      0,
    )
    const at = Math.max(Date.now(), latestCreatedAt)
    dispatch({ type: 'MARK_ALL_READ', at })

    try {
      await httpClient.post('/notifications/mark-all-read')
    } catch {
      // Keep optimistic read state when closing the panel even if sync fails.
    }
  }, [state.items])

  return { state, dispatch, isLoading, error, loadMore, markAllRead }
}
